import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import MainContainer from "../../components/MainContainer";
import SeoHead from "../../components/Seo/SeoHead.js";
import ExploreItem from "../../components/ExploreItem/ExploreItem";
import GenerateCollectionModal from "../../components/GenerateCollectionModal/GenerateCollectionModal";
import EditImageModal from "../../components/EditImageModal/EditImageModal";
import useSeoTemplates from "../../hooks/useSeoTemplates";
import {
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildPageTitle,
  buildWebApplicationSchema,
  buildWebsiteSchema,
} from "../../seo/siteSeo.js";
import { selectLoggedIn } from "../../store/slices/auth/slice.js";
import { setSignUpModalOpen } from "../../store/slices/common/slice.js";
import {
  selectSelectedCollection,
  setSelectedCollection,
} from "../../store/slices/model/slice.js";
import { isImageOutput } from "../../utils/utils.js";
import {
  ActionRow,
  Answer,
  BulletList,
  CardLabel,
  Eyebrow,
  FeatureContent,
  FeatureMedia,
  FeatureRow,
  FeatureStream,
  FeatureText,
  FeatureTitle,
  FaqItem,
  FaqSection,
  Hero,
  Intro,
  LinkCloud,
  LinkPill,
  Page,
  PerkPill,
  PerkRow,
  PrimaryAction,
  PrimaryActionButton,
  PromoCopy,
  PromoSection,
  PromoText,
  PromoTitle,
  Question,
  QuickStepCard,
  QuickStepGrid,
  QuickStepNumber,
  QuickStepText,
  QuickStepTitle,
  QuickStepTop,
  RelatedSection,
  SecondaryAction,
  SecondaryActionButton,
  SectionBlock,
  SectionHeader,
  SectionKicker,
  SectionTitle,
  SupportingCopy,
  TemplateFrame,
  TemplatePlaceholder,
  TemplatesStrip,
  Title,
  TopicCloud,
  TopicPill,
  WaterSeparator,
  WorkflowText,
  WorkflowTitle,
} from "./SeoLandingPage.styles";
import LineWater from "../../assets/images/waterSeparator.webp";

const SeoLandingPage = ({ page }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loggedIn = useSelector(selectLoggedIn);
  const selectedCollection = useSelector(selectSelectedCollection);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const primaryActionLabel = loggedIn ? "Open Fapify" : "Try Fapify Free";
  const [isGenerateCollectionModalOpen, setIsGenerateCollectionModalOpen] =
    useState(false);
  const [isEditImageModalOpen, setIsEditImageModalOpen] = useState(false);
  const { templates, loading: templatesLoading } = useSeoTemplates({
    preferredKeyword: page.keywords?.[0],
    itemCount: Math.max(8, page.sections.length + 4),
  });
  const stripTemplates = templates.slice(0, 4);
  const sectionTemplates = page.sections.map((_, index) => {
    const offset = index + stripTemplates.length;
    return (
      templates[offset] || templates[index % Math.max(templates.length, 1)]
    );
  });

  // Scroll to top whenever the visitor lands on a new SEO page.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [page.path]);

  const handleOpenProduct = () => {
    if (loggedIn) {
      navigate("/");
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      return;
    }

    dispatch(setSignUpModalOpen(true));
  };

  const handleViewPricing = () => {
    if (!loggedIn) {
      dispatch(setSignUpModalOpen(true));
      return;
    }

    navigate("/subscription");
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  };

  const handleTemplateClick = (item) => {
    if (!item) return;

    if (!loggedIn) {
      dispatch(setSignUpModalOpen(true));
      return;
    }

    dispatch(setSelectedCollection(item));

    if (isImageOutput(item.modelType ?? item.modelName)) {
      setIsEditImageModalOpen(true);
    } else {
      setIsGenerateCollectionModalOpen(true);
    }
  };

  const renderTemplate = (item, key) => {
    if (!item || templatesLoading) {
      return (
        <TemplateFrame key={key}>
          <TemplatePlaceholder />
        </TemplateFrame>
      );
    }

    return (
      <div key={key}>
        <ExploreItem
          item={item}
          isMobile={isMobile}
          onItemClick={() => handleTemplateClick(item)}
        />
      </div>
    );
  };

  const jsonLd = [
    buildWebsiteSchema(),
    buildWebApplicationSchema({
      name: `${page.h1} | Fapify`,
      description: page.description,
      url: `https://www.fapify.com${page.path}`,
    }),
    buildBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: page.h1, path: page.path },
    ]),
    buildFaqSchema(page.faq),
  ];

  return (
    <MainContainer hideTabs={false} fullWidth offPadding>
      <SeoHead
        title={buildPageTitle(page.title)}
        description={page.description}
        path={page.path}
        canonicalPath={page.canonicalPath}
        keywords={page.keywords}
        jsonLd={jsonLd}
      />
      <Page>
        {/* ─── 1. Centered hero ─────────────────────────────────────── */}
        <Hero>
          <Eyebrow>Fapify</Eyebrow>
          <Title>{page.h1}</Title>
          <Intro>{page.intro}</Intro>
          {page.supportingCopy ? (
            <SupportingCopy>{page.supportingCopy}</SupportingCopy>
          ) : null}

          <ActionRow $centered>
            <PrimaryActionButton type="button" onClick={handleOpenProduct}>
              {primaryActionLabel}
            </PrimaryActionButton>
            <PrimaryAction to="/">Browse Templates</PrimaryAction>
            <SecondaryActionButton type="button" onClick={handleViewPricing}>
              View Pricing
            </SecondaryActionButton>
          </ActionRow>

          {page.conversion?.perks?.length ? (
            <PerkRow $centered>
              {page.conversion.perks.map((perk) => (
                <PerkPill key={perk}>{perk}</PerkPill>
              ))}
            </PerkRow>
          ) : null}
        </Hero>

        <WaterSeparator src={LineWater} alt="" aria-hidden="true" />

        {/* ─── 2. Live templates strip ──────────────────────────────── */}
        <SectionBlock>
          <SectionHeader>
            <SectionKicker>Live Templates</SectionKicker>
            <WorkflowTitle>Start with what already works</WorkflowTitle>
            <WorkflowText>
              Tap any template to step straight into the Fapify flow with the
              scene already prepared.
            </WorkflowText>
          </SectionHeader>

          <TemplatesStrip>
            {stripTemplates.length
              ? stripTemplates.map((item, index) =>
                  renderTemplate(item, `strip-${item?._id || index}`),
                )
              : [0, 1, 2, 3].map((index) =>
                  renderTemplate(null, `strip-placeholder-${index}`),
                )}
          </TemplatesStrip>
        </SectionBlock>

        {/* ─── 3. How it works ──────────────────────────────────────── */}
        <SectionBlock>
          <SectionHeader>
            <SectionKicker>How It Works</SectionKicker>
            <WorkflowTitle>Three steps from template to clip</WorkflowTitle>
            <WorkflowText>
              Pick a scene that fits, refine the frame, then generate the still
              or short clip — all inside the same workflow.
            </WorkflowText>
          </SectionHeader>

          <QuickStepGrid>
            {page.conversion.steps.map((step, index) => (
              <QuickStepCard key={step.title}>
                <QuickStepTop>
                  <QuickStepNumber>{index + 1}</QuickStepNumber>
                  <QuickStepTitle>{step.title}</QuickStepTitle>
                </QuickStepTop>
                <QuickStepText>{step.text}</QuickStepText>
              </QuickStepCard>
            ))}
          </QuickStepGrid>
        </SectionBlock>

        {/* ─── 4. Feature stream (alternating) ──────────────────────── */}
        <FeatureStream>
          {page.sections.map((section, index) => (
            <FeatureRow key={section.title} $reverse={index % 2 === 1}>
              <FeatureMedia>
                {renderTemplate(
                  sectionTemplates[index],
                  `section-${sectionTemplates[index]?._id || index}`,
                )}
              </FeatureMedia>

              <FeatureContent>
                <CardLabel>{section.label || "Feature"}</CardLabel>
                <FeatureTitle>{section.title}</FeatureTitle>
                {section.intro ? (
                  <FeatureText>{section.intro}</FeatureText>
                ) : null}
                <BulletList>
                  {section.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </BulletList>
              </FeatureContent>
            </FeatureRow>
          ))}
        </FeatureStream>

        {/* ─── 5. Single brand-aqua CTA banner ──────────────────────── */}
        <PromoSection>
          <PromoCopy>
            <PromoTitle>{page.conversion.bannerTitle}</PromoTitle>
            <PromoText>{page.conversion.bannerText}</PromoText>
          </PromoCopy>

          <ActionRow>
            <PrimaryActionButton type="button" onClick={handleOpenProduct}>
              {primaryActionLabel}
            </PrimaryActionButton>
            <SecondaryAction to="/">Browse Templates</SecondaryAction>
          </ActionRow>
        </PromoSection>

        {/* ─── 6. FAQ ──────────────────────────────────────────────── */}
        <FaqSection>
          <SectionTitle>Frequently Asked Questions</SectionTitle>
          {page.faq.map((item) => (
            <FaqItem key={item.question}>
              <Question>{item.question}</Question>
              <Answer>{item.answer}</Answer>
            </FaqItem>
          ))}
        </FaqSection>

        {/* ─── 7. Explore more (light surface, dark text) ──────────── */}
        <RelatedSection>
          <SectionHeader>
            <SectionKicker>Explore More</SectionKicker>
            <WorkflowTitle>Related Fapify pages and topics</WorkflowTitle>
          </SectionHeader>

          <TopicCloud>
            {(page.topics || page.keywords).map((topic) => (
              <TopicPill key={topic}>{topic}</TopicPill>
            ))}
          </TopicCloud>

          <LinkCloud>
            {page.relatedLinks.map((link) => (
              <LinkPill key={link.href} to={link.href}>
                {link.label}
              </LinkPill>
            ))}
          </LinkCloud>
        </RelatedSection>

        <GenerateCollectionModal
          isOpen={isGenerateCollectionModalOpen}
          onClose={() => setIsGenerateCollectionModalOpen(false)}
        />

        <EditImageModal
          isOpen={isEditImageModalOpen}
          collection={selectedCollection}
          onClose={() => setIsEditImageModalOpen(false)}
        />
      </Page>
    </MainContainer>
  );
};

export default SeoLandingPage;
